/**
 * Automation metadata, channel previews, template variables, and UI constants.
 * Pure data — no React components.
 */
import {
  Cake, Clock, UserX, UserPlus, CalendarClock, Zap, Repeat,
  ShoppingBag, Award, Users, MessageCircle, Settings2,
  Star, Trophy, Gamepad2, Flame, Bell, Smartphone, Mail, TrendingUp,
  Gift as GiftIcon,
} from 'lucide-react';

export const AUTOMATION_META = {
  welcome_message: {
    icon: MessageCircle, title: 'Messaggio di benvenuto', category: 'transactional',
    desc: 'Invia un messaggio WhatsApp subito dopo l\'iscrizione',
    color: '#0891B2', bgColor: 'bg-cyan-50', isEventTriggered: true, isTemplate: true,
    templateText: 'Ciao {{nome}}, benvenuto/a da {{locale}}!\nHai ricevuto {{punti}} punti di benvenuto.\n\nAccedi alla tua bacheca personale per vedere punti e premi:\n{{link}}\n\nCi vediamo presto!',
    templateVars: [
      { placeholder: '{{nome}}', label: 'Nome cliente', auto: true },
      { placeholder: '{{locale}}', label: 'Nome locale', auto: true },
      { placeholder: '{{punti}}', label: 'Punti benvenuto', auto: true },
      { placeholder: '{{link}}', label: 'Link bacheca', auto: true },
    ],
    fields: []
  },
  purchase_notification: {
    icon: ShoppingBag, title: 'Notifica punti post visita', category: 'transactional',
    desc: 'Invia un messaggio dopo ogni acquisto con i punti guadagnati',
    color: '#2E7D32', bgColor: 'bg-green-50', isEventTriggered: true, isTemplate: true,
    templateText: 'Ciao {{nome}}, hai appena guadagnato {{punti}} punti sul tuo {{programma}}!\n\nControlla la tua bacheca personale e scopri il tuo nuovo saldo punti.',
    templateVars: [
      { placeholder: '{{nome}}', label: 'Nome cliente', auto: true },
      { placeholder: '{{punti}}', label: 'Punti guadagnati', auto: true },
      { placeholder: '{{programma}}', label: 'Nome programma fedeltà', auto: true },
    ],
    fields: []
  },
  order_confirmation_notification: {
    icon: ShoppingBag, title: 'Conferma ordine online', category: 'transactional',
    desc: 'Invia conferma WhatsApp dopo ogni ordine pagato online (shop, eventi, take-away, delivery) con i punti guadagnati',
    color: '#0E7490', bgColor: 'bg-cyan-50', isEventTriggered: true, isTemplate: true,
    templateText: 'Ciao {{nome}},\n✅ Il tuo ordine {{ordine}} è confermato!\n🎁 Hai guadagnato {{punti}} punti extra!\n\nI punti sono stati aggiunti automaticamente al tuo account e potrai utilizzarli per i tuoi prossimi acquisti o per sbloccare nuovi premi.\n\nA presto!',
    templateVars: [
      { placeholder: '{{nome}}', label: 'Nome cliente', auto: true },
      { placeholder: '{{ordine}}', label: 'Numero ordine', auto: true },
      { placeholder: '{{punti}}', label: 'Punti guadagnati sull\'ordine', auto: true },
    ],
    fields: []
  },
  redeem_notification: {
    icon: Award, title: 'Notifica riscatto premio', category: 'transactional',
    desc: 'Invia un messaggio quando il cliente riscatta un premio',
    color: '#E65100', bgColor: 'bg-orange-50', isEventTriggered: true, isTemplate: true,
    templateText: 'Ciao {{nome}}, hai appena riscattato {{premio}}.\n\nSaldo rimanente: {{saldo}} punti',
    templateVars: [
      { placeholder: '{{nome}}', label: 'Nome cliente', auto: true },
      { placeholder: '{{premio}}', label: 'Premio riscattato', auto: true },
      { placeholder: '{{saldo}}', label: 'Saldo punti rimanente', auto: true },
    ],
    fields: []
  },
  referral_complete_notification: {
    icon: Users, title: 'Referral completato', category: 'transactional',
    desc: 'Avvisa il cliente quando un amico invitato effettua il primo acquisto',
    color: '#1565C0', bgColor: 'bg-blue-50', isEventTriggered: true, isTemplate: true,
    templateText: 'Ciao {{nome}}!\n\n{{amico}} ha appena fatto il suo primo acquisto da {{locale}} grazie a te.\n\nTi abbiamo accreditato {{punti}} punti bonus.',
    templateVars: [
      { placeholder: '{{nome}}', label: 'Nome cliente referrer', auto: true },
      { placeholder: '{{amico}}', label: 'Nome amico invitato', auto: true },
      { placeholder: '{{locale}}', label: 'Nome locale', auto: true },
      { placeholder: '{{punti}}', label: 'Punti bonus referral', auto: true },
    ],
    fields: []
  },
  gift_points_notification: {
    icon: GiftIcon, title: 'Notifica regalo punti', category: 'transactional',
    desc: 'Notifica il cliente quando riceve punti regalo dall\'amministratore',
    color: '#D44E20', bgColor: 'bg-orange-50', isEventTriggered: true, isTemplate: true,
    templateText: 'Ciao {{nome}}, {{locale}} ti ha fatto un regalo!\n\nHai ricevuto {{punti}} punti bonus sul tuo account.\n\nIl tuo nuovo saldo: {{saldo}} punti.',
    templateVars: [
      { placeholder: '{{nome}}', label: 'Nome cliente', auto: true },
      { placeholder: '{{locale}}', label: 'Nome locale', auto: true },
      { placeholder: '{{punti}}', label: 'Punti regalati', auto: true },
      { placeholder: '{{saldo}}', label: 'Nuovo saldo', auto: true },
    ],
    fields: []
  },
  challenge_completed_notification: {
    icon: Trophy, title: 'Challenge completata', category: 'transactional',
    desc: 'Notifica il cliente quando completa una sfida e riceve i punti',
    color: '#F59E0B', bgColor: 'bg-amber-50', isEventTriggered: true, isTemplate: true,
    templateText: 'Ciao {{nome}}, hai completato la sfida!\n\n"{{challenge}}" è stata portata a termine con successo.\n\n{{punti}} punti bonus sono stati accreditati sulla tua bacheca personale.\n\nIl tuo nuovo saldo: {{saldo}} punti.',
    templateVars: [
      { placeholder: '{{nome}}', label: 'Nome cliente', auto: true },
      { placeholder: '{{challenge}}', label: 'Nome challenge', auto: true },
      { placeholder: '{{punti}}', label: 'Punti guadagnati', auto: true },
      { placeholder: '{{saldo}}', label: 'Nuovo saldo', auto: true },
    ],
    fields: []
  },
  onboarding_giochi: {
    icon: Gamepad2, title: 'Onboarding Giochi', category: 'scheduled',
    desc: 'Invita i clienti a scoprire i giochi dopo un certo numero di visite',
    color: '#10B981', bgColor: 'bg-emerald-50', isTemplate: true,
    templateText: 'Ciao {{nome}}!\n\nSapevi che puoi giocare e guadagnare punti extra?\n\nAccedi alla tua bacheca e scopri i giochi disponibili:\n{{link}}\n\nGioca ogni giorno per mantenere la tua streak e sbloccare premi!',
    templateVars: [
      { placeholder: '{{nome}}', label: 'Nome cliente', auto: true },
      { placeholder: '{{link}}', label: 'Link bacheca', auto: true },
    ],
    fields: [
      { key: 'visit_trigger', label: 'Dopo quante visite inviare il messaggio', type: 'number' },
    ]
  },
  streak_reward_notification: {
    icon: Flame, title: 'Notifica premio streak', category: 'transactional',
    desc: 'Notifica il cliente quando raggiunge un traguardo streak e sblocca un premio',
    color: '#EF4444', bgColor: 'bg-red-50', isEventTriggered: true, isTemplate: true,
    templateText: 'Ciao {{nome}}!\n\nComplimenti! Hai raggiunto una streak di {{giorni}} giorni!\n\nHai sbloccato: {{premio}}\n\nContinua a giocare per sbloccare nuovi traguardi!',
    templateVars: [
      { placeholder: '{{nome}}', label: 'Nome cliente', auto: true },
      { placeholder: '{{giorni}}', label: 'Giorni streak', auto: true },
      { placeholder: '{{premio}}', label: 'Descrizione premio', auto: true },
    ],
    fields: []
  },
  review_request: {
    icon: Star, title: 'Richiesta recensione', category: 'scheduled',
    desc: 'Chiedi una recensione ai clienti dopo un certo numero di visite',
    color: '#8B5CF6', bgColor: 'bg-violet-50', isTemplate: true,
    templateText: 'Ciao {{nome}}, grazie per essere tornato/a da {{locale}}!\n\nCi dedichi un minuto per raccontare la tua esperienza?\n\nVai qui {{link}} per lasciare la tua opinione.\nPer noi è molto importante, grazie \u2764\ufe0f',
    templateVars: [
      { placeholder: '{{nome}}', label: 'Nome cliente', auto: true },
      { placeholder: '{{locale}}', label: 'Nome locale', auto: true },
      { placeholder: '{{link}}', label: 'Link piattaforma recensioni', auto: true },
    ],
    fields: [
      { key: 'visit_trigger', label: 'Dopo quante visite inviare la richiesta', type: 'number' },
      { key: 'delay_days', label: 'Giorni di attesa dopo la visita-trigger', type: 'number' },
      { key: 'review_platform', label: 'Piattaforma recensioni', type: 'review_platform_select' },
    ]
  },
  birthday: {
    icon: Cake, title: 'Compleanno', category: 'scheduled',
    desc: 'Invia auguri con un regalo speciale nel giorno del compleanno',
    color: '#E91E63', bgColor: 'bg-pink-50', isTemplate: true,
    templateText: 'Tanti auguri {{nome}}!\n{{locale}} ti fa un regalo speciale per il tuo compleanno:\n{{regalo}}\n\nVieni a trovarci entro {{giorni}} giorni per riceverlo.',
    templateVars: [
      { placeholder: '{{nome}}', label: 'Nome cliente', auto: true },
      { placeholder: '{{locale}}', label: 'Nome locale', auto: true },
      { placeholder: '{{regalo}}', label: 'Descrizione premio configurato', auto: true },
      { placeholder: '{{giorni}}', label: 'Giorni validità configurati', auto: true },
    ],
    fields: [
      { key: 'reward_mode', label: 'Tipo premio', type: 'select', options: [
        { value: 'points', label: 'Solo punti bonus' },
        { value: 'reward', label: 'Solo premio (da lista premi)' },
        { value: 'both', label: 'Punti + premio' },
      ]},
      { key: 'reward_points', label: 'Punti bonus', type: 'number', showIf: (c) => c.reward_mode !== 'reward' },
      { key: 'reward_id', label: 'Premio da accreditare', type: 'reward_select', showIf: (c) => c.reward_mode !== 'points' },
      { key: 'reward_expiry_days', label: 'Giorni per riscattare il premio', type: 'number' },
    ]
  },
  expiry_warning: {
    icon: Clock, title: 'Avviso scadenza punti', category: 'scheduled',
    desc: 'Avvisa i clienti quando i loro punti stanno per scadere',
    color: '#FF9800', bgColor: 'bg-orange-50', isTemplate: true,
    templateText: 'Attenzione {{nome}}!\n\nI tuoi {{punti}} punti scadranno il {{data_scadenza}}.\n\nVieni a utilizzarli prima di questa data.',
    templateVars: [
      { placeholder: '{{nome}}', label: 'Nome cliente', auto: true },
      { placeholder: '{{punti}}', label: 'Saldo punti', auto: true },
      { placeholder: '{{data_scadenza}}', label: 'Data scadenza (calcolata)', auto: true },
    ],
    fields: [
      { key: 'expiry_warn_days', label: 'Giorni di anticipo prima della scadenza', type: 'number' },
    ]
  },
  inactive: {
    icon: UserX, title: 'Cliente inattivo', category: 'scheduled',
    desc: 'Riattiva i clienti che non vengono da un po\'',
    color: '#9C27B0', bgColor: 'bg-purple-50', isTemplate: true,
    templateText: 'Ciao {{nome}}, ci manchi!\n\nTorna a trovarci, hai {{punti}} punti pronti per essere usati.',
    templateVars: [
      { placeholder: '{{nome}}', label: 'Nome cliente', auto: true },
      { placeholder: '{{punti}}', label: 'Saldo punti', auto: true },
    ],
    fields: [
      { key: 'inactive_days', label: 'Giorni di inattività prima dell\'invio', type: 'number' },
    ]
  },
  welcome_followup: {
    icon: UserPlus, title: 'Follow-up benvenuto', category: 'scheduled',
    desc: 'Invita i nuovi clienti che non hanno ancora fatto acquisti',
    color: '#2196F3', bgColor: 'bg-blue-50', isTemplate: true,
    templateText: 'Ciao {{nome}}, ti sei iscritto/a {{giorni}} giorni fa ma ancora non sei tornato/a...\n\nRicorda che qui ci sono i tuoi premi che ti aspettano!',
    templateVars: [
      { placeholder: '{{nome}}', label: 'Nome cliente', auto: true },
      { placeholder: '{{giorni}}', label: 'Giorni dall\'iscrizione', auto: true },
    ],
    fields: [
      { key: 'followup_days', label: 'Giorni dopo la registrazione senza visite', type: 'number' },
    ]
  },
  tier_reminder: {
    icon: TrendingUp, title: 'Reminder prossimo tier', category: 'scheduled',
    desc: 'Avvisa i clienti quando sono vicini al prossimo livello',
    color: '#4CAF50', bgColor: 'bg-green-50', isTemplate: true,
    templateText: 'Ciao {{nome}}, ti mancano solo {{punti_mancanti}} punti per raggiungere il livello {{prossimo_tier}}.\n\nDiventando {{prossimo_tier}} sblocchi: {{benefit}}.\n\nTorna a trovarci, sei a un passo dal traguardo!',
    templateVars: [
      { placeholder: '{{nome}}', label: 'Nome cliente', auto: true },
      { placeholder: '{{punti_mancanti}}', label: 'Punti mancanti', auto: true },
      { placeholder: '{{prossimo_tier}}', label: 'Nome prossimo tier', auto: true },
      { placeholder: '{{benefit}}', label: 'Benefit del tier', auto: true },
    ],
    fields: [
      { key: 'tier_points_threshold', label: 'Punti mancanti per attivare il messaggio', type: 'number' },
    ]
  },
  reward_reminder: {
    icon: Award, title: 'Reminder prossimo premio', category: 'scheduled',
    desc: 'Avvisa i clienti quando sono vicini a sbloccare un premio',
    color: '#FF5722', bgColor: 'bg-orange-50', isTemplate: true,
    templateText: 'Ciao {{nome}}, ti mancano solo {{punti_mancanti}} punti per sbloccare il prossimo premio: {{premio}}.\n\nTorna a trovarci, sei a un passo dal traguardo!',
    templateVars: [
      { placeholder: '{{nome}}', label: 'Nome cliente', auto: true },
      { placeholder: '{{punti_mancanti}}', label: 'Punti mancanti', auto: true },
      { placeholder: '{{premio}}', label: 'Premio più vicino', auto: true },
    ],
    fields: [
      { key: 'reward_points_threshold', label: 'Punti mancanti per attivare il messaggio', type: 'number' },
      { key: 'reward_reminder_cooldown', label: 'Cooldown: giorni minimi tra un reminder e l\'altro', type: 'number' },
    ]
  },
  unclaimed_reward: {
    icon: GiftIcon, title: 'Premio non riscattato', category: 'scheduled',
    desc: 'Ricorda ai clienti di ritirare un premio sbloccato',
    color: '#FF5722', bgColor: 'bg-red-50', isTemplate: true,
    templateText: 'Ciao {{nome}}, hai un premio che ti aspetta da {{giorni}} giorni!\n\n{{premio}} è ancora disponibile nel tuo account.\n\nLa prossima volta, comunicalo in cassa per riceverlo.',
    templateVars: [
      { placeholder: '{{nome}}', label: 'Nome cliente', auto: true },
      { placeholder: '{{giorni}}', label: 'Giorni dallo sblocco', auto: true },
      { placeholder: '{{premio}}', label: 'Premio non riscattato', auto: true },
    ],
    fields: [
      { key: 'unclaimed_days', label: 'Giorni senza riscatto per attivare il messaggio', type: 'number' },
      { key: 'unclaimed_cooldown', label: 'Cooldown: giorni minimi tra un reminder e l\'altro', type: 'number' },
    ]
  },
  tier_benefit_reminder: {
    icon: CalendarClock, title: 'Vantaggi tier in scadenza', category: 'scheduled',
    desc: 'Ricorda di ritirare i vantaggi mensili prima della fine del mese',
    color: '#00BCD4', bgColor: 'bg-cyan-50', isTemplate: true,
    templateText: 'Ciao {{nome}}!\n\nMancano solo {{giorni}} giorni alla fine del mese e hai ancora dei premi {{tier}} da riscattare:\n\n{{lista_premi}}\n\nI premi mensili del {{tier}} non sono cumulabili con quelli del mese successivo quindi non lasciarli scadere o li perderai!',
    templateVars: [
      { placeholder: '{{nome}}', label: 'Nome cliente', auto: true },
      { placeholder: '{{giorni}}', label: 'Giorni a fine mese', auto: true },
      { placeholder: '{{tier}}', label: 'Nome tier cliente', auto: true },
      { placeholder: '{{lista_premi}}', label: 'Lista premi non riscattati', auto: true },
    ],
    fields: [
      { key: 'benefit_reminder_days', label: 'Giorni prima della fine del mese per attivare', type: 'number' },
    ]
  },
  second_visit_month: {
    icon: Zap, title: '2a visita nel mese', category: 'milestones',
    desc: 'Premia automaticamente alla seconda visita nello stesso mese',
    color: '#FF6D00', bgColor: 'bg-amber-50', isEventTriggered: true, isTemplate: true,
    templateText: 'Ciao {{nome}}!\n\nÈ la tua seconda visita questo mese quindi ti abbiamo regalato {{punti}} punti bonus!\n\nLi troverai accreditati sul tuo account.',
    templateVars: [
      { placeholder: '{{nome}}', label: 'Nome cliente', auto: true },
      { placeholder: '{{punti}}', label: 'Punti bonus configurati', auto: true },
    ],
    fields: [
      { key: 'reward_points', label: 'Punti bonus da regalare', type: 'number' },
      { key: 'reward_mode', label: 'Premio aggiuntivo (opzionale)', type: 'select', options: [
        { value: 'points', label: 'Solo punti bonus' },
        { value: 'both', label: 'Punti + premio fisico (accreditato)' },
      ]},
      { key: 'reward_id', label: 'Premio da accreditare', type: 'reward_select', showIf: (c) => c.reward_mode === 'both' },
      { key: 'reward_expiry_days', label: 'Giorni per riscattare il premio', type: 'number', showIf: (c) => c.reward_mode === 'both' },
    ]
  },
  tenth_visit_year: {
    icon: Repeat, title: '10a visita nell\'anno', category: 'milestones',
    desc: 'Premia automaticamente alla 10a visita nell\'anno solare',
    color: '#6A1B9A', bgColor: 'bg-purple-50', isEventTriggered: true,
    fields: [
      { key: 'reward_mode', label: 'Tipo premio', type: 'select', options: [
        { value: 'points', label: 'Solo punti bonus' },
        { value: 'reward', label: 'Solo premio (da lista premi)' },
        { value: 'both', label: 'Punti + premio' },
      ]},
      { key: 'reward_points', label: 'Punti bonus', type: 'number', showIf: (c) => c.reward_mode !== 'reward' },
      { key: 'reward_id', label: 'Premio da accreditare', type: 'reward_select', showIf: (c) => c.reward_mode !== 'points' },
      { key: 'reward_expiry_days', label: 'Giorni per riscattare il premio', type: 'number', showIf: (c) => c.reward_mode !== 'points' },
      { key: 'tenth_visit_message', label: 'Messaggio WhatsApp', type: 'textarea', placeholder: 'Usa {name}, {biz}, {reward_desc}' },
    ]
  },
  coupon_expiry_reminder: {
    icon: Clock, title: 'Reminder scadenza coupon', category: 'scheduled',
    desc: 'Ricorda ai clienti che un coupon sta per scadere (7, 3, 1 giorno prima)',
    color: '#DC2626', bgColor: 'bg-red-50', isTemplate: true,
    templateText: 'Ciao {{1}}.\n\nIl tuo coupon {{2}} scade il {{3}}.\n\nRicorda di utilizzarlo entro questa data per non perdere il tuo vantaggio.',
    templateVars: [
      { placeholder: '{{1}}', label: 'Nome cliente', auto: true },
      { placeholder: '{{2}}', label: 'Nome coupon', auto: true },
      { placeholder: '{{3}}', label: 'Data scadenza', auto: true },
    ],
    fields: [
      { key: 'coupon_ids', label: 'Coupon da monitorare', type: 'coupon_multi_select' },
      { key: 'reminder_cooldown_days', label: 'Cooldown: giorni minimi tra un reminder e l\'altro', type: 'number' },
    ]
  },
};

export const CHANNEL_PREVIEWS = {
  welcome_message: { body: "Ciao {{nome}}, benvenuto/a da {{locale}}! Hai ricevuto {{punti}} punti di benvenuto. Accedi alla tua bacheca per vedere punti e premi.", pushTitle: "Benvenuto/a da {{locale}}!", emailSubject: "Benvenuto/a da {{locale}}!" },
  purchase_notification: { body: "Ciao {{nome}}, hai appena guadagnato {{punti}} punti sul tuo programma fedeltà! Controlla la tua bacheca.", pushTitle: "Hai guadagnato punti!", emailSubject: "Hai guadagnato punti!" },
  order_confirmation_notification: { body: "Ciao {{nome}}, ✅ Il tuo ordine {{ordine}} è confermato! 🎁 Hai guadagnato {{punti}} punti extra! I punti sono stati aggiunti automaticamente al tuo account.", pushTitle: "Ordine {{ordine}} confermato!", emailSubject: "Ordine {{ordine}} confermato — +{{punti}} punti" },
  redeem_notification: { body: "Ciao {{nome}}, hai riscattato {{premio}}. Saldo rimanente: {{saldo}} punti.", pushTitle: "Premio riscattato!", emailSubject: "Premio riscattato!" },
  referral_complete_notification: { body: "Ciao {{nome}}! {{amico}} ha fatto il primo acquisto grazie a te. Ti abbiamo accreditato {{punti}} punti bonus.", pushTitle: "Referral completato!", emailSubject: "Referral completato!" },
  gift_points_notification: { body: "Ciao {{nome}}, {{locale}} ti ha fatto un regalo! Hai ricevuto {{punti}} punti bonus. Nuovo saldo: {{saldo}} punti.", pushTitle: "Punti regalo da {{locale}}!", emailSubject: "Hai ricevuto punti regalo!" },
  challenge_completed_notification: { body: "Ciao {{nome}}, hai completato la sfida \"{{challenge}}\"! {{punti}} punti bonus accreditati. Nuovo saldo: {{saldo}} punti.", pushTitle: "Sfida completata!", emailSubject: "Sfida completata!" },
  streak_reward_notification: { body: "Ciao {{nome}}! Hai raggiunto una streak di {{giorni}} giorni! Hai sbloccato: {{premio}}. Continua a giocare!", pushTitle: "Traguardo streak!", emailSubject: "Traguardo streak raggiunto!" },
  birthday: { body: "Tanti auguri {{nome}}! {{locale}} ti fa un regalo speciale: {{regalo}}. Vieni a trovarci entro {{giorni}} giorni!", pushTitle: "Buon compleanno da {{locale}}!", emailSubject: "Buon compleanno da {{locale}}!" },
  expiry_warning: { body: "Attenzione {{nome}}! I tuoi {{punti}} punti scadranno il {{data_scadenza}}. Vieni a utilizzarli prima di questa data.", pushTitle: "Punti in scadenza!", emailSubject: "I tuoi punti stanno per scadere!" },
  inactive: { body: "Ciao {{nome}}, ci manchi! Torna a trovarci, hai {{punti}} punti pronti per essere usati.", pushTitle: "Ci manchi!", emailSubject: "Ci manchi! Torna a trovarci" },
  welcome_followup: { body: "Ciao {{nome}}, ti sei iscritto/a {{giorni}} giorni fa ma ancora non sei tornato/a. Ricorda che qui ci sono i tuoi premi!", pushTitle: "Ti aspettiamo!", emailSubject: "Ti aspettiamo!" },
  tier_reminder: { body: "Ciao {{nome}}, ti mancano solo {{punti_mancanti}} punti per il livello {{prossimo_tier}}! Sblocchi: {{benefit}}. Torna a trovarci!", pushTitle: "Prossimo livello vicino!", emailSubject: "Sei vicino al prossimo livello!" },
  reward_reminder: { body: "Ciao {{nome}}, ti mancano solo {{punti_mancanti}} punti per sbloccare {{premio}}. Torna a trovarci!", pushTitle: "Premio vicino!", emailSubject: "Sei vicino a sbloccare un premio!" },
  unclaimed_reward: { body: "Ciao {{nome}}, hai un premio che ti aspetta da {{giorni}} giorni: {{premio}}. Vieni a riscattarlo!", pushTitle: "Premio da riscattare!", emailSubject: "Hai un premio che ti aspetta!" },
  tier_benefit_reminder: { body: "Ciao {{nome}}! Mancano solo {{giorni}} giorni a fine mese e hai premi {{tier}} da riscattare: {{lista_premi}}", pushTitle: "Premi mensili in scadenza!", emailSubject: "I tuoi premi mensili scadono presto!" },
  second_visit_month: { body: "Ciao {{nome}}! E' la tua seconda visita questo mese, ti abbiamo regalato {{punti}} punti bonus!", pushTitle: "Punti bonus!", emailSubject: "Punti bonus per la tua seconda visita!" },
  tenth_visit_year: { body: "Complimenti {{nome}}! Hai raggiunto 10 visite quest'anno. Ecco il tuo premio speciale!", pushTitle: "10a visita dell'anno!", emailSubject: "Complimenti per la tua 10a visita!" },
  onboarding_giochi: { body: "Ciao {{nome}}! Sapevi che puoi giocare e guadagnare punti extra? Accedi alla tua bacheca: {{link}}", pushTitle: "Giochi disponibili!", emailSubject: "Scopri i giochi e guadagna punti!" },
  review_request: { body: "Ciao {{nome}}, grazie per essere tornato/a da {{locale}}! Dedicaci un minuto per la tua recensione: {{link}}", pushTitle: "Lascia una recensione!", emailSubject: "Raccontaci la tua esperienza!" },
  coupon_expiry_reminder: { body: "Ciao {{nome}}, il tuo coupon {{coupon}} scade il {{data_scadenza}}. Utilizzalo prima di questa data!", pushTitle: "Coupon in scadenza!", emailSubject: "Il tuo coupon sta per scadere!" },
};

export const AUTOMATION_VARS = {
  welcome_message: [{ key: 'link', label: 'Link bacheca' }, { key: 'programma', label: 'Nome programma' }],
  purchase_notification: [{ key: 'programma', label: 'Nome programma' }],
  order_confirmation_notification: [{ key: 'ordine', label: 'Numero ordine' }],
  redeem_notification: [{ key: 'premio', label: 'Premio riscattato' }, { key: 'saldo', label: 'Saldo rimanente' }],
  referral_complete_notification: [{ key: 'amico', label: 'Nome amico' }],
  gift_points_notification: [{ key: 'saldo', label: 'Nuovo saldo' }],
  challenge_completed_notification: [{ key: 'challenge', label: 'Nome challenge' }, { key: 'saldo', label: 'Nuovo saldo' }],
  streak_reward_notification: [{ key: 'giorni', label: 'Giorni streak' }, { key: 'premio', label: 'Descrizione premio' }],
  birthday: [{ key: 'regalo', label: 'Descrizione premio' }, { key: 'giorni', label: 'Giorni validità' }],
  expiry_warning: [{ key: 'punti_scadenza', label: 'Punti in scadenza' }, { key: 'data_scadenza', label: 'Data scadenza' }],
  inactive: [],
  welcome_followup: [{ key: 'giorni', label: 'Giorni dall\'iscrizione' }],
  tier_reminder: [{ key: 'punti_mancanti', label: 'Punti mancanti' }, { key: 'prossimo_tier', label: 'Prossimo tier' }, { key: 'benefit', label: 'Benefit tier' }],
  reward_reminder: [{ key: 'punti_mancanti', label: 'Punti mancanti' }, { key: 'premio', label: 'Nome premio' }],
  unclaimed_reward: [{ key: 'giorni', label: 'Giorni dallo sblocco' }, { key: 'premio', label: 'Nome premio' }],
  tier_benefit_reminder: [{ key: 'giorni', label: 'Giorni a fine mese' }, { key: 'lista_premi', label: 'Lista premi' }],
  second_visit_month: [],
  tenth_visit_year: [],
  onboarding_giochi: [{ key: 'link', label: 'Link bacheca' }],
  review_request: [{ key: 'link', label: 'Link recensione' }],
  coupon_expiry_reminder: [{ key: 'coupon', label: 'Nome coupon' }, { key: 'data_scadenza', label: 'Data scadenza' }],
};

export const COMMON_VARS = [
  { key: 'nome', label: 'Nome cliente' },
  { key: 'locale', label: 'Nome locale' },
  { key: 'punti', label: 'Saldo punti' },
  { key: 'tier', label: 'Livello fedeltà' },
];

export const CATEGORIES = [
  { key: 'all', label: 'Tutte', icon: Settings2 },
  { key: 'transactional', label: 'Transazionali', icon: Zap },
  { key: 'scheduled', label: 'Programmate', icon: Clock },
  { key: 'milestones', label: 'Traguardi', icon: TrendingUp },
];

export const CH_LIST = [
  { id: 'whatsapp', label: 'WhatsApp', icon: MessageCircle, color: '#25D366' },
  { id: 'push', label: 'Push', icon: Bell, color: '#6366F1' },
  { id: 'sms', label: 'SMS', icon: Smartphone, color: '#F59E0B' },
  { id: 'email', label: 'Email', icon: Mail, color: '#3B82F6' },
];
